import { cssVar } from "@marginly/ui/util/style";
import cn from "classnames";
import { FC, ReactNode, RefObject, useState } from "react";
import { useSwipeable } from "react-swipeable";
import styled from "styled-components";

import { SWIPEABLE_CONFIG } from "@/constants/common";

type ChildFunc = ({ className }: { className?: string }) => ReactNode;
type SwipeableState = "hidden" | "fullscreen";

const Swipeable: FC<{
  className?: string;
  innerTopRef?: RefObject<HTMLDivElement>;
  wrapperRef?: RefObject<HTMLDivElement>;
  children: ReactNode | ChildFunc;
  initialState?: SwipeableState;
}> = ({
  children,
  className,
  initialState = "hidden",
  innerTopRef,
  wrapperRef,
}) => {
  const [_className, setClassName] = useState<SwipeableState>(initialState);

  const handlers = useSwipeable({
    onSwipedDown: () => {
      if (!innerTopRef?.current) {
        setClassName("hidden");
        return;
      }

      const beforeScroll = innerTopRef.current.getBoundingClientRect();

      setTimeout(() => {
        if (!innerTopRef.current) return;
        const afterScroll = innerTopRef.current.getBoundingClientRect();

        if (afterScroll.y - beforeScroll.y < 1) {
          setClassName("hidden");
          wrapperRef?.current?.scrollTo({ top: 0 });
        }
      }, 100);
    },
    onSwipedUp: () => setClassName("fullscreen"),
    onTap: () => setClassName("fullscreen"),
    ...SWIPEABLE_CONFIG,
  });

  return (
    <div className={cn(className, _className)} {...handlers}>
      {typeof children === "function"
        ? children({ className: _className })
        : children}
    </div>
  );
};

export default styled(Swipeable)`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  transition: transform 0.5s ease;
  z-index: 11;
  min-height: calc(100vh - 56px);
  min-height: calc(100dvh - 56px);

  &.hidden {
    transform: translateY(calc(100% - 96px));
  }

  &::before {
    content: " ";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 14px;
    width: 32px;
    height: 4px;
    top: 10px;
    background: ${cssVar("--fill-secondary")};
    z-index: 9;
  }

  @media (min-width: 1024px) {
    position: unset;
    left: unset;
    right: unset;
    bottom: unset;
    padding: unset;
    transition: unset;
    height: fit-content;
    min-height: 0;

    &.hidden {
      transform: unset;
    }
  }

  .counter {
    width: 48px;
    &.disabled {
      cursor: default;
      opacity: 0.24;
      pointer-events: none;
    }
  }

  .controls-field {
    @media (min-width: 1024px) {
      display: none;
    }
  }

  .flip-icon {
    filter: invert(${cssVar("--svg-invert")}) !important;
  }
`;
